import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Spin } from 'antd';
import { hasFortiusAdminRole } from '../../hooks/useIsFortiusAdmin';

// Cross-tenant route guard for /fortius/* pages. Unlike ProtectedRoute, this
// reads the role from the Auth0 token claim rather than the per-tenant
// /auth/sync response — superadmin is a global role, so a user who has it
// should be able to reach client management even when the currently selected
// tenant doesn't exist in dbo.client (the exact scenario these admin pages
// are needed for).
//
// Mounted inside `withAuthenticationRequired`, so `isAuthenticated` is
// already true by the time this renders — no unauthenticated branch.
export const FortiusProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isLoading, user } = useAuth0();

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <Spin size="large" />
            </div>
        );
    }

    if (!hasFortiusAdminRole(user as Record<string, unknown> | undefined)) {
        // Diagnostic: surface the claim keys the Auth0 ID token actually
        // contains so we can adjust ROLE_CLAIM_KEYS / FORTIUS_ADMIN_ROLE if
        // the configured Auth0 Rule/Action ever changes the shape. Logs
        // keys only — not values — to keep PII (email, sub, picture) out
        // of the browser console in production.
        console.warn('[FortiusProtectedRoute] superadmin role not found; claim keys =',
            user ? Object.keys(user) : 'no user');
        return <Navigate to="/unauthorized" replace />;
    }

    return <>{children}</>;
};
